import type { DirectoryFilters } from '@/components/client/directory/use-directory-reducer'

import type { DirectoryOffer, DirectoryProduct, DirectoryRelease } from './aggregate-releases'

const matchesFilter = (productValues: string[], selectedValues: string[]): boolean => {
  if (selectedValues.length === 0) {
    return true
  }
  return selectedValues.every((value) => productValues.includes(value))
}

export const hasProductFilters = (filters: DirectoryFilters): boolean =>
  filters.classification.length > 0 ||
  filters.tags.length > 0 ||
  filters.channels.length > 0 ||
  filters.hardware.length > 0 ||
  filters.software.length > 0 ||
  filters.loss.length > 0 ||
  filters.style.length > 0 ||
  filters.accessories.length > 0

const productMatchesFilters = (product: DirectoryProduct, filters: DirectoryFilters): boolean => {
  if (filters.classification.length > 0 && !filters.classification.includes(product.classification)) {
    return false
  }
  if (!matchesFilter(product.tags, filters.tags)) {
    return false
  }
  if (!matchesFilter(product.channels, filters.channels)) {
    return false
  }
  if (filters.hardware.length + filters.software.length > 0) {
    const matchesFeatures =
      filters.featureMode === 'or'
        ? filters.hardware.some((value) => product.hardware.includes(value)) ||
          filters.software.some((value) => product.software.includes(value))
        : matchesFilter(product.hardware, filters.hardware) && matchesFilter(product.software, filters.software)
    if (!matchesFeatures) {
      return false
    }
  }
  if (!matchesFilter(product.loss, filters.loss)) {
    return false
  }
  if (filters.style.length > 0 && !filters.style.includes(product.style)) {
    return false
  }
  if (!matchesFilter(product.accessories, filters.accessories)) {
    return false
  }
  return true
}

export const productsMatchingFilters = (products: DirectoryProduct[], filters: DirectoryFilters): DirectoryProduct[] =>
  products.filter((product) => productMatchesFilters(product, filters))

export const pickBestDirectoryProduct = (products: DirectoryProduct[]): DirectoryProduct => {
  return [...products].sort((a, b) => {
    // Highest real HT score first, so a scored card always shows the tested
    // product's image. Within an unrated subset, prefer its primary model.
    if (a.htScore == null && b.htScore != null) {
      return 1
    }
    if (a.htScore != null && b.htScore == null) {
      return -1
    }
    if (a.htScore != null && b.htScore != null && a.htScore !== b.htScore) {
      return b.htScore - a.htScore
    }
    const ap = a.primary ? 0 : 1
    const bp = b.primary ? 0 : 1
    if (ap !== bp) {
      return ap - bp
    }
    const al = a.level ?? 999
    const bl = b.level ?? 999
    if (al !== bl) {
      return al - bl
    }
    return a.slug.localeCompare(b.slug)
  })[0]
}

/**
 * Narrows a release-family card to the products matching the active product
 * filters. The selected product supplies both its score and image; an unrated
 * filtered model therefore shows its own image with no score badge.
 */
export const presentReleaseForFilters = (
  release: DirectoryRelease,
  filters: DirectoryFilters
): DirectoryRelease | null => {
  if (!hasProductFilters(filters)) {
    return release
  }

  const matchingProducts = productsMatchingFilters(release.products, filters)
  if (matchingProducts.length === 0) {
    return null
  }

  const cardProduct = pickBestDirectoryProduct(matchingProducts)
  const matchingModelIds = new Set<number>()
  let fromPrice: number | null = null
  let offer: DirectoryOffer | null = null
  for (const product of matchingProducts) {
    if (product.modelId != null) {
      matchingModelIds.add(product.modelId)
    }
    if (product.fromPrice != null && (fromPrice == null || product.fromPrice < fromPrice)) {
      fromPrice = product.fromPrice
      offer = product.offer
    }
  }

  return {
    ...release,
    cardSlug: cardProduct.slug,
    score: cardProduct.htScore,
    soundScore: cardProduct.soundScore,
    image: cardProduct.image,
    fromPrice,
    offer,
    quietAudioUrl: cardProduct.quietAudioUrl,
    modelCount: matchingModelIds.size,
  }
}
